"use client";

import { Flex, Spinner } from "@chakra-ui/react";
import type { MouseEvent } from "react";
import { Link, Text, Button } from "@/components";
import { useInteractiveTokens } from "@/lib/theme/tokens/interactive";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useClassRegistrationCta } from "../hooks/useClassRegistrationCta";

export interface ClassRegisterCtaProps {
  classId: number;
  allowOnlineRegistration?: boolean | null;
  /** `hero` - single class page; `compact` - list/grid; `sticky` - mobile bottom bar */
  variant?: "hero" | "compact" | "sticky";
  /** Hero without cover image - inverted CTA so it does not blend into accent gradient. */
  heroBackdrop?: "image" | "gradient";
  /** Stretch compact/sticky button to fill its flex/grid cell (e.g. paired with another CTA). */
  fullWidth?: boolean;
  /** Override default button label */
  label?: string;
  /** Button size for compact/sticky variants */
  size?: "sm" | "md";
  "data-testid"?: string;
}

export const ClassRegisterCta = ({
  classId,
  allowOnlineRegistration,
  variant = "compact",
  heroBackdrop = "image",
  fullWidth = false,
  label,
  size,
  "data-testid": dataTestId,
}: ClassRegisterCtaProps) => {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();
  const {
    showCta,
    isSessionLoading,
    registerHref,
    isAuthenticated,
  } = useClassRegistrationCta(classId, allowOnlineRegistration);

  if (!showCta) return null;

  const stopRowClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  if (variant === "compact" || variant === "sticky") {
    const isSticky = variant === "sticky";
    const buttonLabel =
      label ?? (isSticky ? "Register for this class" : "Register");
    const buttonSize = size ?? (isSticky ? "md" : "sm");
    const sharedButtonProps = {
      variant: "primary" as const,
      size: buttonSize,
      w: isSticky || fullWidth ? "100%" : undefined,
      minW: fullWidth ? 0 : undefined,
      maxW: fullWidth ? "100%" : undefined,
      fontSize: "sm" as const,
      px: 4,
      borderRadius: "8px !important",
      "data-testid": dataTestId ?? "class-register-cta",
      onClick: stopRowClick,
    };

    if (isSessionLoading) {
      return (
        <Button
          {...sharedButtonProps}
          isLoading
          loadingText="Loading"
          isDisabled
        >
          {buttonLabel}
        </Button>
      );
    }

    if (!registerHref) {
      return (
        <Button {...sharedButtonProps} isDisabled>
          {buttonLabel}
        </Button>
      );
    }

    return (
      <Button as={Link} href={registerHref} {...sharedButtonProps}>
        {buttonLabel}
      </Button>
    );
  }

  if (isSessionLoading) {
    return (
      <Flex
        h="44px"
        px={6}
        align="center"
        justify="center"
        borderRadius="8px"
        bg={heroBackdrop === "gradient" ? "white" : "whiteAlpha.300"}
        boxShadow={heroBackdrop === "gradient" ? "md" : undefined}
        data-testid={dataTestId ?? "class-register-cta-loading"}
      >
        <Spinner
          size="sm"
          color={heroBackdrop === "gradient" ? theme.accent.primary : "white"}
        />
      </Flex>
    );
  }

  if (!registerHref) return null;

  const isGradientHero = heroBackdrop === "gradient";

  return (
    <Flex direction="column" gap={2} align="flex-start">
      <Button
        as={Link}
        href={registerHref}
        h="44px"
        px={6}
        fontSize="sm"
        fontWeight={isGradientHero ? "600" : "400"}
        borderRadius="8px !important"
        bg={isGradientHero ? "white" : theme.accent.primary}
        color={isGradientHero ? theme.accent.primary : "white"}
        data-testid={dataTestId ?? "class-register-cta"}
        sx={{
          textDecoration: "none",
          "@media (hover: hover)": {
            "&:hover": {
              bg: isGradientHero ? "whiteAlpha.900" : interactive.button.primary.hover,
              color: isGradientHero ? theme.accent.primary : "white",
              textDecoration: "none",
              transform: isGradientHero ? "translateY(-1px)" : undefined,
            },
          },
        }}
      >
        Register for this class
      </Button>
      {!isAuthenticated && (
        <Text fontSize="xs" color="rgba(255,255,255,0.8)" lineHeight="1.4">
          Sign in required to complete registration
        </Text>
      )}
    </Flex>
  );
};
